import { interval, Observable, Subject } from 'rxjs';
import { take, map, tap, takeUntil } from 'rxjs/operators';

import {
  Input,
  Output,
  OnInit,
  Component,
  ViewChild,
  OnDestroy,
  EventEmitter,
  ChangeDetectionStrategy,
} from '@angular/core';
import { MatStepper } from '@angular/material/stepper';

import { ISnActivationError } from './activate.interfaces';
import { ActivateService } from './activate.service';

@Component({
  selector: 'sn-confirm-container',
  templateUrl: './activate.container.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ActivateService],
})
export class SnActivateContainer implements OnInit, OnDestroy {
  @Input()
  public uid: string;

  @Input()
  public token: string;

  @Output()
  public readonly confirmError = new EventEmitter<ISnActivationError>();

  @Output()
  public readonly confirm = new EventEmitter();

  @Output()
  public readonly serverError = new EventEmitter<any>();

  public readonly redirectTimer$: Observable<number>;

  public emailIsConfirmed = false;
  public phoneIsConfirmed = false;
  public passwordIsCreated = false;
  public passwordWasAlreadyCreated = false;
  public phoneConfirmationRequired = true;

  @ViewChild('stepper')
  private readonly _stepper: MatStepper;

  private readonly _destroy$ = new Subject<void>();

  constructor(private readonly _activateService: ActivateService) {
    this.redirectTimer$ = this._generateTimer();
  }

  public ngOnInit(): void {
    this._activateService.activationIsFail$
      .pipe(takeUntil(this._destroy$))
      .subscribe((error) => this.confirmError.emit(error));
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  public onEmailConfirmed(res: any): void {
    this.emailIsConfirmed = true;
    setTimeout(() => this._stepper.next());
    if (res.phoneConfirmationRequired === false) {
      this.phoneConfirmationRequired = false;
      this.phoneIsConfirmed = true;
      setTimeout(() => this._stepper.next());
    }
    if (res.passwordCreated) {
      this.passwordWasAlreadyCreated = true;
      this.passwordIsCreated = true;
      setTimeout(() => this._stepper.next());
    }
  }

  public onPhoneConfirmed(): void {
    this.phoneIsConfirmed = true;
    setTimeout(() => this._stepper.next());
  }

  public onPasswordCreated(): void {
    this.passwordIsCreated = true;
    setTimeout(() => this._stepper.next());
  }

  public completeConfirmation(): void {
    this.confirm.emit();
  }

  private _generateTimer(): Observable<number> {
    return interval(1000).pipe(
      take(6),
      map((index: number) => 5 - index),
      tap({
        complete: () => this.completeConfirmation(),
      }),
      takeUntil(this._destroy$),
    );
  }
}
